import { ToTop } from '../ui/ToTop';
import { useTuck } from '../ui/useTuck';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { BOOKS_CHANGED } from '../lib/changed';
import { keep, useRemembered } from '../lib/remember';
import { myBooksTab } from '../mybooks/model';
import { supabase } from '../lib/supabase';
import { useNotice } from '../shell/notice';
import { Popover } from '../ui/Popover';
import { ChipRow, FilterChip, InterestDots } from '../ui/bits';
import { ListRow, ListTable, type Col } from '../ui/ListTable';
import { Sheet } from '../book/reading';
import { INTEREST_HINTS, interestHint } from '../book/changes';
import { bookLabel, todayIn } from '../book/model';
import { loadDetails, loadFilterFacts, loadShelves, type Details, type Shelves } from '../shelves/load';
import { buildGroups, filterCount, matches, pruneSubgenres, recentFrom, type FilterFacts, type FilterLists, type FilterState } from '../shelves/filters';
import { FilterBar, FilterPanel } from '../shelves/Filters';
import { BookSheet, type BookSheetKind } from './BookSheet';
import { RevisitList } from './RevisitList';
import { useFlag, useMatches } from '../shell/hooks';
import { SettingMenu } from '../ui/PillMenu';
import { TabRow } from '../ui/TabRow';
import { ListFoot } from '../ui/ListFoot';
import { BulkMenu, BulkPlaceSheet, InterestMenu, MoveMenu, MoveToNumber, NARROW_INTEREST, NotForMeSheet, RowBook, RowCheck, SelectShown, TypeMenu, flipSort, interestItems, readTypeItems } from './parts';
import { AuthorLine } from '../ui/NameLink';
import { moveBook, moveBooks, placeBook, placeBooks, setBook, setBooks, type Place } from './actions';
import {
  PAGE,
  QUICK,
  SORTS,
  TABS,
  afterDrop,
  blockTo,
  canReorder,
  copyText,
  dropPlace,
  moveTo,
  booksText,
  num,
  otherShelf,
  rowsOf,
  selectedText,
  shelfName,
  sortRows,
  queueTab,
  tabDef,
  WAITING_HINT,
  waitingSince,
  waitingText,
  type ListTab,
  type QueueRow,
  type QueueTab,
  type QuickKey,
  type Sort,
  type SortKey,
} from './model';
import '../shelves/shelves.css';
import './queue.css';

// The Queue (Phase 6.3 Part 1, agreed with Kristi Oct 29): the full list for Up Next and Future
// reads. A pinned bar (tabs, Filters, Sort, Select several), quick filters above the list, then
// the list: Rank · Book · Read type · Interest · Goodreads · time waiting · Copy · Move ▾. Drag ⠿
// or click a rank to reorder (Your order only); Select several for changes to many at once. Every
// change has Undo. Revisit (Part 2) is RevisitList; Recently added (Part 3) is RecentList.

type Load = { kind: 'loading' } | { kind: 'error'; message: string } | { kind: 'ready'; shelves: Shelves };

/** Back from a book on a phone: the same tab, page and spot. */
const back = { y: 0, shown: new Map<string, number>() };

export function QueuePage({ wide }: { wide: boolean }) {
  // On a phone the pinned bar slides away scrolling down, back scrolling up (check-up, Kristi Nov 2).
  const tucked = useTuck(wide);
  // Up to 1,000 px (an upright iPad, a phone) Interest is a small pill with a menu: two clicks,
  // half the room of the five dots (Kristi, Oct 30). Wider screens keep the dots.
  const narrowInterest = useMatches(NARROW_INTEREST);
  // From 700 to 1,000 px some columns fold under the author (the same width as Interest's pill).
  const fold = wide && narrowInterest;
  const notice = useNotice();
  const today = todayIn();
  const { path } = useRoute();
  const [state, setState] = useState<Load>({ kind: 'loading' });
  const [facts, setFacts] = useState<{ facts: FilterFacts[]; lists: FilterLists } | null>(null);
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  const [readTypes, setReadTypes] = useState<string[]>([]);
  const [tabRaw, setTab] = useRemembered<QueueTab>('queue:tab', 'up_next');
  const tab = queueTab(tabRaw);
  const listTab: ListTab | null = tab === 'up_next' || tab === 'future_reads' ? tab : null;
  const [sort, setSort] = useRemembered<Sort>(`queue:sort:${tab}`, { key: 'rank', reversed: false });
  const [filters, setFilters] = useRemembered<FilterState>(`queue:filters:${tab}`, {});
  const [quick, setQuick] = useRemembered<QuickKey[]>(`queue:quick:${tab}`, []);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [shown, setShown] = useState(back.shown.get(tab) ?? PAGE);
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [sheet, setSheet] = useState<{ kind: 'not_for_me'; ids: string[]; title: string } | null>(null);
  const [bookSheet, setBookSheet] = useState<{ row: QueueRow; what: BookSheetKind } | null>(null);
  const editOn = useFlag('book_page.edit');
  const [drag, setDrag] = useState<{ id: string; over: string | null; lower: boolean } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  // ↑ Top once you've scrolled more than a screen (Kristi, Oct 29; the shared piece since Nov 2).
  const topPill = <ToTop wide={wide} />;
  // The pinned bar's height, so the column headers can pin just under it.
  const topEl = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = topEl.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--queue-top-h', `${el.offsetHeight}px`);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  // The shelves first, then what the filters and the Copy column know (formats, loans).
  const load = () =>
    loadShelves()
      .then((shelves) => {
        setState({ kind: 'ready', shelves });
        void loadFilterFacts(shelves, todayIn())
          .then(setFacts)
          .catch(() => null);
      })
      .catch((e: unknown) => setState({ kind: 'error', message: e instanceof Error ? e.message : String(e) }));

  useEffect(() => {
    void load();
    void supabase
      ?.from('read_types')
      .select('name, sort_order')
      .order('sort_order')
      .then(({ data }) => setReadTypes(((data as { name: string }[] | null) ?? []).map((r) => r.name)));
    // A change anywhere (a row, the side panel, Add a book): read the list again.
    const again = () => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void load(), 250);
    };
    window.addEventListener(BOOKS_CHANGED, again);
    return () => window.removeEventListener(BOOKS_CHANGED, again);
  }, []);

  // A new tab starts at its first page, with nothing selected.
  const pickTab = (t: QueueTab) => {
    setTab(t);
    setShown(back.shown.get(t) ?? PAGE);
    setSelected([]);
    setSelecting(false);
    setFiltersOpen(false);
  };

  const [toolsSlot, setToolsSlot] = useState<HTMLElement | null>(null);
  const [panelSlot, setPanelSlot] = useState<HTMLElement | null>(null);
  const factsById = useMemo(() => new Map(facts?.facts.map((f) => [f.id, f]) ?? []), [facts]);
  const from = facts ? recentFrom(today, facts.lists.recentDays) : today;
  const shelf = state.kind === 'ready' && listTab ? state.shelves[listTab] : [];
  const tabFacts = useMemo(() => {
    const ids = new Set(shelf.map((b) => b.id));
    return facts?.facts.filter((f) => ids.has(f.id)) ?? [];
  }, [facts, shelf]);
  const all = useMemo(() => rowsOf(shelf, factsById), [shelf, factsById]);
  const quickDefs = listTab ? QUICK[listTab] : [];
  // The Shelves panel without its Shelf row: the tab is the shelf. Its chips count within the quick
  // chips that are on too (filters review F10, Kristi Nov 2), so a count is what tapping it shows.
  const panelFacts = useMemo(() => {
    const picked = quickDefs.filter((q) => quick.includes(q.key));
    if (!picked.length) return tabFacts;
    const ids = new Set(all.filter((b) => picked.every((q) => q.test(b, today))).map((b) => b.id));
    return tabFacts.filter((f) => ids.has(f.id));
  }, [tabFacts, all, quick, listTab, today]);
  const groups = useMemo(() => (facts ? buildGroups(panelFacts, facts.lists, filters, from).filter((g) => g.key !== 'shelf') : []), [facts, panelFacts, filters, from]);
  const rows = useMemo(() => {
    if (!listTab) return [];
    const on = filterCount(filters) > 0;
    const picked = quickDefs.filter((q) => quick.includes(q.key));
    const kept = all.filter((b) => (!on || (factsById.get(b.id) && matches(factsById.get(b.id)!, filters, from))) && picked.every((q) => q.test(b, today)));
    return sortRows(kept, sort, listTab);
  }, [all, filters, quick, sort, listTab, factsById, from, today]);
  // Each quick chip counts within what the Filters panel leaves (R7 N3, Kristi Nov 2: answer 4a),
  // so a chip never says [12] while 3 rows appear.
  const filtered = useMemo(() => (filterCount(filters) > 0 ? all.filter((b) => factsById.get(b.id) && matches(factsById.get(b.id)!, filters, from)) : all), [all, filters, factsById, from]);
  const quickCount = (k: QuickKey) => filtered.filter((b) => quickDefs.find((q) => q.key === k)!.test(b, today)).length;
  const reorder = canReorder(sort);
  const narrowed = filterCount(filters) > 0 || quick.length > 0;

  // The rows on screen, and their authors and covers (read as they come into view).
  const visible = rows.slice(0, shown);
  useEffect(() => {
    const missing = visible.filter((b) => !details.has(b.id));
    if (!missing.length) return;
    let live = true;
    loadDetails(missing, new Set())
      .then((m) => live && setDetails((old) => new Map([...old, ...m])))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [visible.map((b) => b.id).join(',')]);

  // Show all: more rows as the end of the list comes into view.
  const end = useRef<HTMLDivElement>(null);
  const [allOn, setAllOn] = useState(false);
  useEffect(() => {
    if (!allOn || shown >= rows.length || !end.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((e) => e.some((x) => x.isIntersecting) && setShown((n) => n + 100), { rootMargin: '800px' });
    io.observe(end.current);
    return () => io.disconnect();
  }, [allOn, shown, rows.length]);

  // Back from a book (phone): the same spot.
  useEffect(() => {
    if (!wide && back.y && state.kind === 'ready') {
      const y = back.y;
      back.y = 0;
      window.requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [state.kind]);
  const open = (b: QueueRow) => () => {
    if (!wide) {
      back.y = window.scrollY;
      back.shown.set(tab, shown);
    }
    navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide });
  };

  // ---- changes, shown at once and then saved ----
  const reshelve = (ids: string[]) => {
    if (state.kind !== 'ready' || !listTab) return;
    const byId = new Map(state.shelves[listTab].map((b) => [b.id, b]));
    setState({ kind: 'ready', shelves: { ...state.shelves, [listTab]: ids.map((id) => byId.get(id)!).filter(Boolean) } });
  };
  const patch = (id: string, change: Partial<QueueRow>) => {
    if (state.kind !== 'ready' || !listTab) return;
    setState({ kind: 'ready', shelves: { ...state.shelves, [listTab]: state.shelves[listTab].map((b) => (b.id === id ? { ...b, ...change } : b)) } });
  };
  const fullIds = () => all.map((b) => b.id);
  const place = (b: QueueRow, p: Place, n: number) => {
    reshelve(moveTo(fullIds(), b.id, n));
    void placeBook(notice, b, p, `${b.title} moved to #${num(Math.min(n, all.length))} on ${shelfName(listTab!)}`);
  };
  const toTop = (b: QueueRow) => place(b, { place: 'top' }, 1);
  const toBottom = (b: QueueRow) => place(b, { place: 'bottom' }, all.length);
  const toNumber = (b: QueueRow, n: number) => place(b, { place: n }, n);
  const dropped = (id: string, over: string, lower: boolean) => {
    const p = dropPlace(rows.map((r) => r.id), id, over, lower);
    if (!p) return;
    const ids = afterDrop(fullIds(), id, p);
    const b = all.find((x) => x.id === id)!;
    reshelve(ids);
    void placeBook(notice, b, p, `${b.title} moved to #${num(ids.indexOf(id) + 1)} on ${shelfName(listTab!)}`);
  };
  const setInterest = (b: QueueRow, v: number | null) => {
    patch(b.id, { interest: v });
    void setBook(notice, b, 'interest', v, v === null ? `Interest cleared for ${b.title}` : `Interest set to ${interestHint(v)}`);
  };
  const setType = (b: QueueRow, name: string | null) => {
    const key = (name ?? '').toLowerCase();
    patch(b.id, { readTypeName: name, readType: key === 'light' || key === 'absorbing' || key === 'demanding' || key === 'study' ? key : null });
    void setBook(notice, b, 'read_type', name, name ? `${b.title}: ${name}` : `Read type cleared for ${b.title}`);
  };

  // ---- Select several ----
  const isSel = (id: string) => selected.includes(id);
  const toggleSel = (id: string) => setSelected(isSel(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const shownIds = visible.map((b) => b.id);
  const inOrder = (ids: string[]) => rows.map((r) => r.id).filter((id) => ids.includes(id));
  // Several books at once, the same pattern as one (Kristi, Oct 29): to Up Next or Future reads
  // asks where on the shelf; Reference asks "Should they be Study?" in the notice.
  const bulkMove = (to: ListTab | 'reference', place?: 'kept' | 'top' | 'bottom') => {
    const ids = inOrder(selected);
    reshelve(fullIds().filter((id) => !ids.includes(id)));
    setSelected([]);
    const study = readTypes.find((n) => n.trim().toLowerCase() === 'study');
    void moveBooks(
      notice,
      ids,
      to,
      place ? { place: { place } } : {},
      `moved to ${shelfName(to)}`,
      to === 'reference' && study ? { label: 'Make them Study', run: () => void setBooks(notice, ids, 'read_type', study, `set to ${study}`) } : undefined,
    );
  };
  const [bulkPlace, setBulkPlace] = useState<ListTab | null>(null);
  // Several books to a new place on this shelf, as a block (0041; Kristi, Oct 29).
  const [bulkNumber, setBulkNumber] = useState(false);
  const bulkWithin = (where: 'top' | 'bottom' | number) => {
    const ids = inOrder(selected);
    const order = blockTo(fullIds(), ids, where);
    reshelve(order);
    const first = order.indexOf(ids[0]!) + 1;
    const n = ids.length;
    void placeBooks(notice, ids, where, `${n === 1 ? '1 book' : `${num(n)} books`} moved to ${n === 1 ? `#${num(first)}` : `#${num(first)}–${num(first + n - 1)}`} on ${shelfName(listTab!)}`);
  };
  const picked = all.filter((b) => selected.includes(b.id));
  const bulkSet = (field: 'interest' | 'read_type', value: number | string | null, what: string) => {
    const ids = inOrder(selected);
    for (const id of ids) patch(id, field === 'interest' ? { interest: value as number | null } : { readTypeName: value as string | null });
    void setBooks(notice, ids, field, value, what);
  };

  const sorts = listTab ? SORTS[listTab] : [];
  const sortBy = (k: SortKey) => {
    setSort(flipSort(sort, k));
    setShown(PAGE);
  };
  const when = listTab === 'up_next' ? 'On Up Next' : 'On your list';
  // The one table (R6): every column named but Move ▾'s (T3); ⇅ on each that sorts (T4). From 700
  // to 1,000 px (an upright iPad) ★, time waiting and Copy fold under the author, so the title keeps its room.
  const cols: Col<SortKey>[] = [
    ...(selecting ? [{ label: <SelectShown shownIds={shownIds} selected={selected} setSelected={setSelected} />, width: '26px' }] : []),
    { width: '22px' },
    { label: 'Rank', width: '54px', sort: 'rank' as SortKey },
    { label: 'Book', width: fold ? 'minmax(0, 1fr)' : 'minmax(0, 2.6fr)', sort: 'title' as SortKey, book: true },
    { label: 'Read type', width: '128px', sort: 'readtype' as SortKey },
    { label: 'Interest', width: 'var(--q-interest-w)', sort: 'interest' as SortKey },
    ...(fold
      ? []
      : [
          { label: '★', width: '62px', sort: 'goodreads' as SortKey, num: true, title: 'Goodreads' },
          // "Waiting" in the header so it never wraps; the full words ("On Up Next …") in its tip.
          { label: 'Waiting', width: '92px', sort: 'waiting' as SortKey, num: true, title: listTab ? `${when}: ${WAITING_HINT[listTab]}` : when },
          { label: 'Copy', width: 'minmax(0, 1fr)', sort: 'copy' as SortKey },
        ]),
    { width: '84px' },
  ];

  return (
    <div class={`page queue${wide ? '' : ' narrow-list'}${tucked ? ' tucked' : ''}`}>
      {wide ? <h1 class="visually-hidden">Queue</h1> : null}
      <div class="shelves-top queue-top" ref={topEl}>
        {/* No counts on tabs (R7 N1, Kristi Nov 2: answer 1a): each tab's count is the grey line under its description. */}
        <TabRow<QueueTab> label="Queue" tabs={TABS.map((t) => ({ key: t.key, name: t.name }))} value={tab} onPick={pickTab} />
        {listTab && state.kind === 'ready' ? (
          <>
            <span class="shelves-divider" aria-hidden="true" />
            <FilterBar
              groups={groups}
              state={filters}
              open={filtersOpen}
              onToggle={() => setFiltersOpen(!filtersOpen)}
              onChange={(s) => {
                setFilters(facts ? pruneSubgenres(s, facts.lists) : s);
                setShown(PAGE);
              }}
              showing={narrowed ? rows.length : null}
            />
            <span class="queue-top-end">
              <SettingMenu<SortKey>
                name="Sort"
                choices={sorts}
                value={sort.key}
                onChange={(k) => {
                  setSort({ key: k, reversed: false });
                  setShown(PAGE);
                }}
              />
              <button
                type="button"
                class={`btn-secondary queue-select${selecting ? ' on' : ''}`}
                aria-pressed={selecting}
                aria-label="Select several"
                onClick={() => {
                  setSelecting(!selecting);
                  setSelected([]);
                }}
              >
                {/* Filled teal while on (R5: fill = on). */}
                {wide ? 'Select several' : 'Select'}
              </button>
              {topPill}
            </span>
          </>
        ) : null}
        {/* Revisit and Recently added put Filters, Sort and Select several here too, and their Filters
            panel under them, as on Up Next (Kristi, Oct 30): each tab fills these two slots. */}
        {!listTab ? <span class="queue-slot" ref={setToolsSlot} /> : null}
        {!listTab ? <span class="queue-slot" ref={setPanelSlot} /> : null}
        {listTab && filtersOpen && state.kind === 'ready' ? (
          facts ? (
            <FilterPanel
              groups={groups}
              state={filters}
              onChange={(s) => setFilters(pruneSubgenres(s, facts.lists))}
              onClose={() => setFiltersOpen(false)}
              showing={rows.length}
            />
          ) : (
            <p class="shelves-quiet">Loading the filters…</p>
          )
        ) : null}
      </div>

      {tab === 'revisit' ? (
        state.kind === 'loading' ? (
          <p class="shelves-quiet">Loading your Queue…</p>
        ) : state.kind === 'error' ? (
          <div class="heads-up">Couldn’t load your Queue: {state.message}</div>
        ) : (
          <RevisitList wide={wide} shelves={state.shelves} facts={facts} readTypes={readTypes} slots={{ tools: toolsSlot, panel: panelSlot }} top={topPill} />
        )
      ) : !listTab ? null : state.kind === 'loading' ? (
        <p class="shelves-quiet">Loading your Queue…</p>
      ) : state.kind === 'error' ? (
        <div class="heads-up">Couldn’t load your Queue: {state.message}</div>
      ) : (
        <>
          <div class="tab-head">
            <p class="queue-description">{tabDef(tab).description}</p>
            {/* The tab's count, in grey (R7 N2, Kristi Nov 2: answer 2a): nothing on the Queue needs you. */}
            <p class="mb-needs quiet">{booksText(all.length)}</p>
          </div>
          {/* Recently added moved to My books (Kristi, Oct 31): a quiet way there from Future reads. */}
          {listTab === 'future_reads' ? <NoStatusLine count={state.shelves.no_status.length} /> : null}
          {/* Filter chips (R3 P2): none on is the whole list; each counts within what Filters leaves;
              a chip at 0 hides unless it's on. On a phone the row scrolls with the edge (R9 M2, M5). */}
          <div class="queue-quick">
            <ChipRow label="Quick filters" scroll={!wide}>
              {quickDefs.map((q) => {
                const on = quick.includes(q.key);
                return (
                  <FilterChip
                    key={q.key}
                    label={q.label}
                    count={quickCount(q.key)}
                    on={on}
                    title={q.key === 'two_years' && listTab ? `${q.hint}${WAITING_HINT[listTab]}` : q.hint}
                    onToggle={() => {
                      setQuick(on ? quick.filter((k) => k !== q.key) : [...quick, q.key]);
                      setShown(PAGE);
                    }}
                  />
                );
              })}
            </ChipRow>
          </div>
          {!reorder && wide ? (
            <p class="queue-note">
              Sorted by {sorts.find(([k]) => k === sort.key)?.[1] ?? 'reading order'}{sort.reversed ? ', reversed' : ''}: dragging is off.{' '}
              <button type="button" class="btn-link" onClick={() => setSort({ key: 'rank', reversed: false })}>
                Sort by reading order
              </button>
            </p>
          ) : null}
          <ListTable<SortKey>
            label={shelfName(listTab)}
            class={`queue-list${selecting ? ' selecting' : ''}`}
            cols={cols}
            sort={{ key: sort.key, desc: sort.reversed }}
            onSort={sortBy}
            empty={all.length ? 'Nothing here matches.' : `Nothing on ${shelfName(listTab)} yet.`}
            onClear={
              all.length
                ? () => {
                    setFilters({});
                    setQuick([]);
                    setShown(PAGE);
                  }
                : undefined
            }
            foot={
              rows.length ? (
                <>
                  <div ref={end} aria-hidden="true" />
                  <ListFoot
                    shown={allOn ? rows.length : Math.min(shown, rows.length)}
                    total={rows.length}
                    page={PAGE}
                    extra={narrowed ? `${num(all.length)} on ${shelfName(listTab)}` : undefined}
                    onMore={() => setShown(shown + PAGE)}
                    onAll={() => {
                      setAllOn(true);
                      setShown(shown + 100);
                    }}
                    onFewer={() => {
                      setAllOn(false);
                      setShown(PAGE);
                      window.scrollTo({ top: 0 });
                    }}
                  />
                </>
              ) : null
            }
          >
            {visible.map((raw) => {
              const b = { ...raw, ...details.get(raw.id), owned: raw.owned };
              const copy = copyText(b);
              const over = drag?.over === b.id && drag.id !== b.id;
              const gr = b.goodreads ? `★ ${b.goodreads.toFixed(2)}` : null;
              const check = selecting ? <RowCheck title={b.title} on={isSel(b.id)} onToggle={() => toggleSel(b.id)} /> : null;
              const handle = <span class="q-handle">{reorder ? <DragHandle row={b} rows={rows} drag={drag} setDrag={setDrag} onDrop={dropped} /> : null}</span>;
              const rank = (
                <span class="q-rank">
                  {reorder ? <RankButton row={b} max={all.length} onMove={(n) => toNumber(b, n)} onTop={() => toTop(b)} onBottom={() => toBottom(b)} /> : <span class="q-rank-text">#{num(b.rank)}</span>}
                </span>
              );
              // The phone, and an upright iPad, fold ★, time waiting and Copy into the line under the author.
              const meta = fold || !wide ? [gr, `${when} ${waitingText(waitingSince(b, listTab), today)}`, copy ?? 'Not owned yet'].filter(Boolean).join(' · ') : null;
              const book = <RowBook title={b.title} sub={<AuthorLine author={b.author} authorId={b.authorId} rest={[b.series]} />} cover={b.cover} meta={meta} onOpen={open(b)} />;
              const type = (
                <span class="q-type">
                  <TypeMenu row={b} names={readTypes} onPick={(n) => setType(b, n)} />
                </span>
              );
              const move = (
                <span class="q-move">
                  <MoveMenu
                    row={b}
                    pastRead={Boolean(editOn)}
                    onSheet={(what) => setBookSheet({ row: b, what })}
                    within={{ shelf: shelfName(listTab), rank: b.rank, max: all.length, reorder, onTop: () => toTop(b), onBottom: () => toBottom(b), onNumber: (n) => toNumber(b, n) }}
                  />
                </span>
              );
              return (
                <ListRow
                  key={b.id}
                  data-row={b.id}
                  class={`q-row${drag?.id === b.id ? ' dragging' : ''}${over ? (drag!.lower ? ' drop-after' : ' drop-before') : ''}`}
                  selected={isSel(b.id)}
                  cells={[
                    ...(selecting ? [check] : []),
                    handle,
                    rank,
                    book,
                    type,
                    <span class="q-interest">
                      {narrowInterest ? <InterestMenu row={b} onPick={(v) => setInterest(b, v)} /> : <InterestDots value={b.interest} hints={INTEREST_HINTS} onChange={(v) => setInterest(b, v)} />}
                    </span>,
                    ...(fold
                      ? []
                      : [
                          <span class="q-gr" title="Goodreads">
                            {gr ?? '–'}
                          </span>,
                          <span class="q-when">{waitingText(waitingSince(b, listTab), today)}</span>,
                          <span class="q-copy">{copy ? copy : <span class="tile-notowned">Not owned yet</span>}</span>,
                        ]),
                    move,
                  ]}
                  phone={{
                    lead: (
                      <span class="q-lead">
                        {check}
                        {handle}
                        {rank}
                      </span>
                    ),
                    book,
                    control: move,
                    line: (
                      <span class="q-controls">
                        {type}
                        <span class="q-interest">
                          <InterestMenu row={b} onPick={(v) => setInterest(b, v)} />
                        </span>
                      </span>
                    ),
                  }}
                />
              );
            })}
          </ListTable>
          {selecting ? (
            <div class="queue-bulk" role="region" aria-label="Change the selected books">
              <span class="queue-bulk-count">{selectedText(selected.length)}</span>
              {selected.length ? (
                <>
                  <BulkMenu
                    label="Move"
                    note="Start reading and Finished: one book at a time, from its own Move ▾."
                    items={[
                      { label: `${shelfName(otherShelf(listTab))}…`, run: () => setBulkPlace(otherShelf(listTab)) },
                      { label: 'Not for me…', run: () => setSheet({ kind: 'not_for_me', ids: inOrder(selected), title: selected.length === 1 ? (rows.find((r) => r.id === selected[0])?.title ?? '1 book') : `${num(selected.length)} books` }) },
                      { label: 'Reference', run: () => bulkMove('reference') },
                      { heading: `Within ${shelfName(listTab)}` },
                      { label: '⤒ To the top', run: () => bulkWithin('top') },
                      { label: '⤓ To the bottom', run: () => bulkWithin('bottom') },
                      { label: 'Move to #…', run: () => setBulkNumber(true) },
                    ]}
                  />
                  <BulkMenu label="Read type" items={readTypeItems(readTypes, picked, bulkSet)} />
                  <BulkMenu label="Interest" items={interestItems(picked, bulkSet)} />

                </>
              ) : (
                <span class="queue-bulk-hint">Tick books, or select every book that matches.</span>
              )}
              <span class="queue-bulk-end">
                {rows.length > selected.length ? (
                  <button type="button" class="btn-link" onClick={() => setSelected(rows.map((r) => r.id))}>
                    Select all {num(rows.length)}
                  </button>
                ) : null}
                {selected.length ? (
                  <button type="button" class="btn-link" onClick={() => setSelected([])}>
                    Clear
                  </button>
                ) : null}
              </span>
            </div>
          ) : null}
        </>
      )}
      <ToTop wide={wide} float above={selecting} />
      {bookSheet ? <BookSheet label={bookSheet.row.label} title={bookSheet.row.title} what={bookSheet.what} onClose={() => setBookSheet(null)} /> : null}
      {bulkNumber && listTab ? (
        <BulkNumberSheet
          count={selected.length}
          max={all.length}
          shelf={shelfName(listTab)}
          onClose={() => setBulkNumber(false)}
          onMove={(n) => {
            setBulkNumber(false);
            bulkWithin(n);
          }}
        />
      ) : null}
      {bulkPlace ? (
        <BulkPlaceSheet
          to={bulkPlace}
          count={selected.length}
          onClose={() => setBulkPlace(null)}
          onMove={(place) => {
            setBulkPlace(null);
            bulkMove(bulkPlace, place);
          }}
        />
      ) : null}
      {sheet ? (
        <NotForMeSheet
          title={sheet.title}
          many={sheet.ids.length > 1}
          onClose={() => setSheet(null)}
          onSave={async (chips, reason) => {
            const details = { chips, reason, where: 'queue' };
            const ids = sheet.ids;
            reshelve(fullIds().filter((id) => !ids.includes(id)));
            setSelected([]);
            const ok =
              ids.length === 1
                ? await moveBook(notice, { id: ids[0]!, title: sheet.title }, 'not_for_me', details, `${sheet.title} moved to Not for me`)
                : await moveBooks(notice, ids, 'not_for_me', details, 'moved to Not for me');
            if (ok) setSheet(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** ⠿: press and drag (mouse or finger) to a new place among the rows showing. */
function DragHandle({
  row,
  rows,
  drag,
  setDrag,
  onDrop,
}: {
  row: QueueRow;
  rows: QueueRow[];
  drag: { id: string; over: string | null; lower: boolean } | null;
  setDrag: (d: { id: string; over: string | null; lower: boolean } | null) => void;
  onDrop: (id: string, over: string, lower: boolean) => void;
}) {
  const latest = useRef(drag);
  latest.current = drag;
  // The latest rows and drop, for listeners added once per drag.
  const now = useRef({ rows, onDrop, setDrag });
  now.current = { rows, onDrop, setDrag };
  // Not ended when ⠿ is redrawn: the drag carries on until you let go (that is the point of it).
  const stop = useRef<(() => void) | null>(null);
  const where = (x: number, y: number) => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-row]');
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { over: el.dataset.row!, lower: y > r.top + r.height / 2 };
  };
  // The drag listens on the whole window, not only on ⠿ (check-up, Kristi Nov 2: the drop line showed
  // but letting go did nothing once the handle lost hold of the pointer mid-drag). Letting go anywhere
  // ends it; the browser cancelling it, or Escape, ends it without a move.
  const begin = (pointerId: number) => {
    stop.current?.();
    const move = (e: PointerEvent) => {
      if (e.pointerId !== pointerId || latest.current?.id !== row.id) return;
      // Near the top or bottom of the window, the page scrolls along.
      if (e.clientY < 90) window.scrollBy(0, -12);
      else if (e.clientY > window.innerHeight - 70) window.scrollBy(0, 12);
      const w = where(e.clientX, e.clientY);
      if (w && (w.over !== latest.current.over || w.lower !== latest.current.lower)) now.current.setDrag({ id: row.id, ...w });
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const d = latest.current;
      end();
      if (!d || d.id !== row.id) return;
      const w = where(e.clientX, e.clientY) ?? (d.over ? { over: d.over, lower: d.lower } : null);
      if (w && now.current.rows.some((r) => r.id === w.over)) now.current.onDrop(row.id, w.over, w.lower);
    };
    const cancel = (e: PointerEvent) => {
      if (e.pointerId === pointerId) end();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') end();
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('keydown', key);
      stop.current = null;
      now.current.setDrag(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('keydown', key);
    stop.current = end;
  };
  return (
    <button
      type="button"
      class="q-drag"
      aria-label={`Drag ${row.title} to a new place`}
      title="Drag to a new place"
      onPointerDown={(e) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        setDrag({ id: row.id, over: null, lower: false });
        begin(e.pointerId);
      }}
    >
      ⠿
    </button>
  );
}

/** The rank: click it to Move to #… (with To the top and To the bottom). */
function RankButton({ row, max, onMove, onTop, onBottom }: { row: QueueRow; max: number; onMove: (n: number) => void; onTop: () => void; onBottom: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span class="pop-host">
      <button type="button" class="q-rank-btn" aria-haspopup="dialog" aria-expanded={open} aria-label={`#${row.rank}: move ${row.title} to another place`} onClick={() => setOpen(!open)}>
        #{num(row.rank)}
      </button>
      <Popover open={open} onClose={() => setOpen(false)} label={`Move ${row.title}`} sheetTitle={`Move ${row.title}`}>
        <MoveToNumber
          title={row.title}
          rank={row.rank}
          max={max}
          onMove={(n) => {
            setOpen(false);
            onMove(n);
          }}
          onTop={() => {
            setOpen(false);
            onTop();
          }}
          onBottom={() => {
            setOpen(false);
            onBottom();
          }}
          onCancel={() => setOpen(false)}
        />
      </Popover>
    </span>
  );
}

/** Move to #… for several books: the first of them at #n, the rest straight after (0041). */
function BulkNumberSheet({ count, max, shelf, onClose, onMove }: { count: number; max: number; shelf: string; onClose: () => void; onMove: (n: number) => void }) {
  const [n, setN] = useState('');
  const [err, setErr] = useState('');
  const go = () => {
    const v = Number(n);
    if (!/^\d+$/.test(n.trim()) || v < 1) return setErr(`A number from 1 to ${num(max)}`);
    onMove(Math.min(v, max));
  };
  return (
    <Sheet title={`Move ${count === 1 ? '1 book' : `${num(count)} books`} on ${shelf}`} sub="The first goes to the number you give, the rest straight after it, in this list’s sequence." onClose={onClose}>
      <label class="q-moveto-field">
        <span>Move to #</span>
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={max}
          value={n}
          autoFocus
          onInput={(e) => {
            setN((e.target as HTMLInputElement).value);
            setErr('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && go()}
        />
      </label>
      {err ? <p class="q-err">{err}</p> : null}
      <div class="bk-buttons">
        <button type="button" class="btn-main" onClick={go}>
          Move
        </button>
        <button type="button" class="btn-secondary" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Sheet>
  );
}


/**
 * Under Future reads' description (Kristi, Oct 31; words Nov 1): books with no shelf yet. The link
 * opens My books › Update details on its first list, No shelf yet (filters review, Kristi Nov 2),
 * which shows every such book, imports included, so the list matches the count.
 */
function NoStatusLine({ count }: { count: number }) {
  if (!count) return null;
  const href = myBooksTab('review').path;
  return (
    <p class="queue-nostatus-line">
      {/* A link says what you'll do there, then where (check-up, Kristi Nov 2). */}
      {count === 1 ? '1 book has no shelf yet.' : `${num(count)} books have no shelf yet.`}{' '}
      <a
        class="btn-link"
        href={href}
        onClick={(e) => {
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          keep('queue:review:chip', 'shelf');
          navigate(href);
        }}
      >
        {count === 1 ? 'Give it a shelf in Update details ›' : 'Give them a shelf in Update details ›'}
      </a>
    </p>
  );
}
